// Full-document render of the toppers ProductDisplayPage (ROUTABLE): PageSkeleton
// (the React document shell) wrapping the ``product_display_page.html``
// layout -- the pdp-hero (a ``<picture>`` with server-resolved renditions + the
// modular configurator), the two disjoint module islands, the promo bar, and the
// gated product-reviews island.
//
// Server-only values (image renditions, the configurator SSR envelope, the
// ``get_solo`` PromoSettings promo bar, the reviews SSR fetch) are resolved
// server-side and threaded as scalar / nested-template side-channels. The promo
// bar's mobile column interleaves raw ``|safe`` richtext with a SHOP NOW anchor as
// flat siblings, so its inner HTML is built server-side (React cannot mix
// dangerouslySetInnerHTML with sibling children).
//
// The iovation scripts are injected imperatively from a post-hydration effect
// (IovationScripts) rather than as declarative React <script>s: React 19 would
// hoist a declarative ``<script async src>`` snare to <head>, letting it execute
// before the body IGLOO config on a warm cache and silently degrading
// fingerprinting.
import React from "react";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";

import { Context, templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { ProductDisplayPageBody } from "../components/ProductDisplayPageBody";
import { ProductDisplayPageOffer } from "../components/ProductDisplayPageOffer";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";
import { Template as ProductReviews } from "./ProductReviewsTemplate";

// Mutable reactivated props (not Immutable<>): this template composes the in-tree
// SiteHeader / SiteFooter chain via PageSkeleton, which is typed with mutable
// reactivated props. The islands accept Immutable props, to which these are
// assignable.
type IProps = templates.ProductDisplayPageTemplate;

const renderHeroPicture = (props: IProps): React.JSX.Element | null => {
    // ``{% if page.hero_image %}`` -- the whole ``<picture>`` is omitted otherwise.
    if (props.hero_image_url === null) {
        return null;
    }
    return (
        <picture className="pdp-hero__image">
            {props.mobile_hero_image_url !== null ? (
                <source
                    srcSet={props.mobile_hero_image_url}
                    media="(max-width: 767px)"
                />
            ) : null}
            <img
                src={props.hero_image_url}
                alt={props.hero_image_alt}
                fetchPriority="high"
            />
        </picture>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page } = props;
    const chrome = React.useContext(Context).chrome;

    // ``{% if page.modules %}`` guards are StreamValue truthiness (an empty
    // stream is falsy). Both body islands share the gate.
    const hasModules = !!page.modules && page.modules.value.length > 0;

    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div id="product-detail-container">
                <div id="product-detail">
                    <div className="pdp-hero">
                        {renderHeroPicture(props)}
                        <div className="pdp-hero__content l-capped-width">
                            <ModularConfigurator {...props.configurator} />
                        </div>
                    </div>

                    {/* Body modules: all module types EXCEPT ``offer`` (the client
                        map filters ``offer`` out; it renders in the disjoint offer
                        island below, the two-loop split). */}
                    {hasModules ? <ProductDisplayPageBody page={page} /> : null}

                    {/* The outer ``.promo-bar`` always renders; an empty promo_bar
                        list reproduces an empty ``get_solo PromoSettings`` loop. */}
                    <div className="promo-bar pdp-promo-bar l-full-width">
                        {props.promo_bar.map((item, i) => (
                            <React.Fragment key={i}>
                                <div
                                    className="promo-bar__copy"
                                    dangerouslySetInnerHTML={{
                                        __html: item.copy_html,
                                    }}
                                />
                                <div
                                    className="promo-bar__copy--mobile"
                                    dangerouslySetInnerHTML={{
                                        __html: item.mobile_inner_html,
                                    }}
                                />
                                <div className="promo-bar__button">
                                    <a
                                        className="promo-bar__button__link"
                                        href={item.shop_now_url}
                                    >
                                        <p>SHOP NOW</p>
                                    </a>
                                    <div className="promo-bar__button__diagonal" />
                                </div>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* ``{% if page.reviews_brand_id %}{% react_product_reviews
                        page.products %}`` -- the product-reviews island. */}
                    {props.reviews !== null ? (
                        <ProductReviews {...props.reviews} />
                    ) : null}

                    {/* Offer modules: ONLY the ``offer`` key, after the reviews
                        section (second loop). */}
                    {hasModules ? (
                        <ProductDisplayPageOffer page={page} />
                    ) : null}
                </div>
            </div>

            {/* iovation (page-scoped): injected post-hydration to keep the
                IGLOO -> snare -> loader execution order (see IovationScripts). */}
            <IovationScripts
                iovationUrl={props.iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
